import React, { useState, useEffect, useRef } from 'react';
import { 
  Activity, 
  Play, 
  Square, 
  Settings, 
  TrendingUp,
  Clock,
  ChevronDown,
  ChevronUp,
  Plus,
  Minus,
  CheckCircle,
  AlertCircle,
  Bot,
  DollarSign,
  BarChart3,
  LineChart,
  Moon,
  Sun,
} from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { 
  AVAILABLE_BOTS, 
  DESIRED_SYMBOLS, 
  calculateSMA, 
  calculateEMA, 
  calculateRSI, 
  calculateStdDev,
  calculateChange,
  getMarketSession,
  validateSLTPDistance,
  validateMaxRisk,
  MIN_SL_TP_DISTANCE
} from '../utils/tradingUtils';
import MetaApi from 'metaapi.cloud-sdk';
import { metaApiService } from '../services/MetaApiService';

// Magic number for identifying bot trades
const MAGIC_NUMBER = 202402;

// Check current open positions on symbol
const getPositionsOnSymbol = (positions, symbol, magic) => {
  return positions.filter(p => 
    p.magic === magic && 
    (p.symbol === symbol || p.symbol.includes(symbol.replace('USD', '')))
  );
};

// Initial empty prices
const initialPrices = {};
DESIRED_SYMBOLS.forEach(symbol => {
  initialPrices[symbol] = {
    symbol,
    bid: '—',
    ask: '—',
    change: '—',
    direction: 'neutral'
  };
});

  // Get token from environment - with fallback
const HARDCODED_TOKEN = 'eyJhbGciOiJSUzUxMiIsInR5cCI6IkpXVCJ9.eyJfaWQiOiIwYTY2NzM5YWRlMDRmYjE2ZDE3ZjIxNmM5ZWM5NDFjZiIsImFjY2Vzc1J1bGVzIjpbeyJpZCI6InRyYWRpbmctYWNjb3VudC1tYW5hZ2VtZW50LWFwaSIsIm1ldGhvZHMiOlsidHJhZGluZy1hY2NvdW50LW1hbmFnZW1lbnQtYXBpOnJlc3Q6cHVibGljOio6KiJdLCJyb2xlcyI6WyJyZWFkZXIiLCJ3cml0ZXIiXSwicmVzb3VyY2VzIjpbIio6JFVTRVJfSUQkOioiXX0seyJpZCI6Im1ldGFhcGktcmVzdC1hcGkiLCJtZXRob2RzIjpbIm1ldGFhcGktYXBpOnJlc3Q6cHVibGljOio6KiJdLCJyb2xlcyI6WyJyZWFkZXIiLCJ3cml0ZXIiXSwicmVzb3VyY2VzIjpbIio6JFVTRVJfSUQkOioiXX0seyJpZCI6Im1ldGFhcGktcnBjLWFwaSIsIm1ldGhvZHMiOlsibWV0YWFwaS1hcGk6d3M6cHVibGljOio6KiJdLCJyb2xlcyI6WyJyZWFkZXIiLCJ3cml0ZXIiXSwicmVzb3VyY2VzIjpbIio6JFVTRVJfSUQkOioiXX0seyJpZCI6Im1ldGFhcGktcmVhbC10aW1lLXN0cmVhbWluZy1hcGkiLCJtZXRob2RzIjpbIm1ldGFhcGktYXBpOndzOnB1YmxpYzoqOioiXSwicm9sZXMiOlsicmVhZGVyIiwid3JpdGVyIl0sInJlc291cmNlcyI6WyIqOiRVU0VSX0lEJDoqIl19LHsiaWQiOiJtZXRhc3RhdHMtYXBpIiwibWV0aG9kcyI6WyJtZXRhc3RhdHMtYXBpOnJlc3Q6cHVibGljOio6KiJdLCJyb2xlcyI6WyJyZWFkZXIiLCJ3cml0ZXIiXSwicmVzb3VyY2VzIjpbIio6JFVTRVJfSUQkOioiXX0seyJpZCI6InJpc2stbWFuYWdlbWVudC1hcGkiLCJtZXRob2RzIjpbInJpc2stbWFuYWdlbWVudC1hcGk6cmVzdDpwdWJsaWM6KjoqIl0sInJvbGVzIjpbInJlYWRlciIsIndyaXRlciJdLCJyZXNvdXJjZXMiOlsiKjokVVNFUl9JRCQ6KiJdfSx7ImlkIjoiY29weWZhY3RvcnktYXBpIiwibWV0aG9kcyI6WyJjb3B5ZmFjdG9yeS1hcGk6cmVzdDpwdWJsaWM6KjoqIl0sInJvbGVzIjpbInJlYWRlciIsIndyaXRlciJdLCJyZXNvdXJjZXMiOlsiKjokVVNFUl9JRCQ6KiJdfSx7ImlkIjoibXQtbWFuYWdlci1hcGkiLCJtZXRob2RzIjpbIm10LW1hbmFnZXItYXBpOnJlc3Q6ZGVhbGluZzoqOioiLCJtdC1tYW5hZ2VyLWFwaTpyZXN0OnB1YmxpYzoqOioiXSwicm9sZXMiOlsicmVhZGVyIiwid3JpdGVyIl0sInJlc291cmNlcyI6WyIqOiRVU0VSX0lEJDoqIl19LHsiaWQiOiJiaWxsaW5nLWFwaSIsIm1ldGhvZHMiOlsiYmlsbGluZy1hcGk6cmVzdDpwdWJsaWM6KjoqIl0sInJvbGVzIjpbInJlYWRlciJdLCJyZXNvdXJjZXMiOlsiKjokVVNFUl9JRCQ6KiJdfV0sImlnbm9yZVJhdGVMaW1pdHMiOmZhbHNlLCJ0b2tlbklkIjoiMjAyMTAyMTMiLCJpbXBlcnNvbmF0ZWQiOmZhbHNlLCJyZWFsVXNlcklkIjoiMGE2NjczOWFkZTA0ZmIxNmQxN2YyMTZjOWVjOTQxY2YiLCJpYXQiOjE3NzI0NjQyMjZ9.SyEw47dBxqKbjifZUSriLVRtxXGT4uTD-oUCjTy3noT0dAEpZFiSmxjROlOTvnvTaOQSdclzmS2okRSpYrVOHQLmSuF8CNKYMm_iay8IBojYSV5P4qZrjhlI0zua--kxQ2B_hIxBC494lHVvK7AGGBxpc2DLjgDpxOBey5AF9LMF2XQOoxrghLeb3XCxSORuZpO1jgBaWVh-f8MqK9A6AXdQFDNlIsp3Nf2QnsH3WI2v0TXxeaU1VJCint8NrDZGO3Fm-XmiddpLJ4hY4JR_TcT-ueFdi8bb7MHcZedAcrcMowRIqBVT0_Js6VhcmfbxfBVKfu5lF-pkkn-0uUh0BoZKY-f0J8JMaYWhhzijzpueZ-9xFRZR74KYY-cyUd_AEsRZdbxxkt4wBx6cQpD2phl7YcPZ9IyDpCvqnhawIfjUU_msFbgJ7_ilKIQFmEEAbwzZFbZaWhWLspazjDmHYyiIwxFxRXzSd8xc61vnxcS5-lvpu3neSiaV97Qfp9pvBsD1soWoEVywxQkYMmf6Ppb6r7cDgIVHMi4OrJaiug8AvWKnsu_zjuUAHgnu1iunKUSyy24Hu9M12Ui7upAm2WwIlkiQMqIBDPMOZNzca7VEG8ZiTYNJmA983wU-6rNX6HI0y8sqcuoDYGjADAe0CEwNqGNFAOy6Qdp2mucLJj4';

const getMetaApiToken = () => {
  // First try environment variables
  const envToken = process.env.REACT_APP_METAAPI_TOKEN || process.env.REACT_APP_FALLBACK_TOKEN;
  if (envToken) return envToken;
  
  // Fallback to hardcoded token
  console.log('Using hardcoded token fallback');
  return HARDCODED_TOKEN;
};  


export default function Dashboard() {
  const { 
    isLicenseValid, 
    mtAccount, 
    isRunning, 
    startRobot, 
    stopRobot, 
    selectedBot, 
    setSelectedBot,
    tradeSettings,
    setTradeSettings,
    isDarkMode,
    toggleTheme,
    accountBalance,

  } = useStore();

  // MetaApi SDK instance for Dashboard
  const metaApiRef = useRef(null);
  const accountRef = useRef(null);

  // Initialize MetaApi connection when account is available
  useEffect(() => {
    const initializeMetaApi = async () => {
      if (!mtAccount?.login) return;
      
      try {
        setConnectionStatus('Connecting...');
        console.log('Dashboard: Initializing MetaApi with account:', mtAccount.login);
        
        const token = getMetaApiToken();
        console.log('Token available:', !!token);
        console.log('Token type:', typeof token);
        console.log('Token constructor:', token?.constructor?.name);
        console.log('Token is primitive string:', typeof token === 'string');
        console.log('Token valueOf:', typeof token?.valueOf);
        
        // Ensure it's a primitive string
        const primitiveToken = String(token);
        console.log('Primitive token type:', typeof primitiveToken);
        
        const api = new MetaApi(primitiveToken);
        const accounts = await api.metatraderAccountApi.getAccount([]);
        const accs = accounts._data;
        
        const matched = accs.find((acc) => acc.login === mtAccount.login);
        if (!matched) {
          console.log('Account not found, creating new...');
          throw new Error('Account not found');
        }
        
        const account = await api.metatraderAccountApi.getAccount(matched._id);
        
        // Deploy if needed
        if (['DEPLOYING', 'UNDEPLOYED'].includes(account.state)) {
          await account.deploy();
          await account.waitDeployed({ timeoutInSeconds: 120 });
        }
        
        await account.waitConnected({ timeoutInSeconds: 180 });
        
        if (account.connectionStatus !== 'CONNECTED') {
          throw new Error(`Connection failed: ${account.connectionStatus}`);
        }
        
        metaApiRef.current = api;
        accountRef.current = account;
        
        // Note: metaApiService REST API fails due to CORS, but SDK WebSocket works
        // We'll use the SDK's RPC connection directly for trading instead
        
        setConnectionStatus('Connected');
        console.log('Dashboard: MetaApi connected successfully');
        
        // Fetch initial positions after connection
        try {
          const rpc = account.getRPCConnection();
          await rpc.connect();
          const positions = await rpc.getPositions();
          setOpenPositions(positions);
          console.log('Initial positions loaded:', positions.length);
        } catch (err) {
          console.warn('Failed to fetch initial positions:', err);
        }
        
      } catch (err) {
        console.error('Dashboard: MetaApi init failed:', err);
        setConnectionStatus('Disconnected');
      }
    };
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    initializeMetaApi();
  }, [mtAccount?.login]);


  const [activeTab, setActiveTab] = useState('status');
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [expandedSymbol, setExpandedSymbol] = useState(null);
  const [snackbar, setSnackbar] = useState({ visible: false, message: '' });
  
  // Live quotes state
  const [liveQuotes, setLiveQuotes] = useState(initialPrices);
  const [priceHistory, setPriceHistory] = useState({});
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  
  // Trade tracking - use metaApiService
  const [openPositions, setOpenPositions] = useState([]);
  
  // Individual trade tracking with detailed info for concurrent trades
  const [individualTrades, setIndividualTrades] = useState({});
  const [lastTradeTime, setLastTradeTime] = useState(0);
  
  // Technical indicators
  const [indicatorValues, setIndicatorValues] = useState({});
  
  // Market session
  const [currentSession, setCurrentSession] = useState({ name: 'Unknown', isActive: false });
  
  // Trade log state
  // eslint-disable-next-line no-unused-vars
  const [tradeLog, setTradeLog] = useState([]);
  // const [showTradeLog, setShowTradeLog] = useState(false);

  // Local settings state
  const [localSettings, setLocalSettings] = useState({
    lotSize: tradeSettings?.lotSize?.toString() || "0.01",
    action: tradeSettings?.action || "BOTH",
    tradeEveryMinutes: tradeSettings?.tradeEveryMinutes?.toString() || "5",
    triggerPoints: tradeSettings?.triggerPoints?.toString() || "10",
    riskPercent: tradeSettings?.riskPercent?.toString() || "1",
    maxOpenTrades: tradeSettings?.maxOpenTrades?.toString() || "3",
    useSLTP: tradeSettings?.useSLTP ?? true,
  });

  const [profitTargetsUSD, setProfitTargetsUSD] = useState(
    tradeSettings?.profitTargetsUSD || {
      EURUSD: "8", GBPUSD: "9", USDJPY: "8", XAUUSD: "7", BTCUSD: "10"
    }
  );

  const [stopLossUSD, setStopLossUSD] = useState(
    tradeSettings?.stopLossUSD || {
      EURUSD: "12", GBPUSD: "14", USDJPY: "12", XAUUSD: "10", BTCUSD: "12"
    }
  );

  // Real price updates from MetaApi SDK
  useEffect(() => {
    const fetchRealPrices = async () => {
      if (!accountRef.current) {
        // Account not ready yet
        return;
      }

      try {
        const rpc = accountRef.current.getRPCConnection();
        await rpc.connect();
        
        let anySuccess = false;

        for (const symbol of DESIRED_SYMBOLS) {
          try {
            const price = await rpc.getSymbolPrice(symbol);
            if (price && price.bid != null && price.ask != null) {
              anySuccess = true;
              const newBid = parseFloat(price.bid);
              const isGold = symbol === 'XAUUSD';
              const isJpy = symbol.includes('JPY');

              // Use functional update to avoid stale closure
              setLiveQuotes(prev => {
                const currentBid = parseFloat(prev[symbol]?.bid) || 0;
                return {
                  ...prev,
                  [symbol]: {
                    ...prev[symbol],
                    symbol,
                    bid: isGold ? newBid.toFixed(2) : isJpy ? newBid.toFixed(3) : newBid.toFixed(5),
                    ask: isGold ? parseFloat(price.ask).toFixed(2) : isJpy ? parseFloat(price.ask).toFixed(3) : parseFloat(price.ask).toFixed(5),
                    change: calculateChange(currentBid, newBid),
                    direction: newBid > currentBid ? 'up' : newBid < currentBid ? 'down' : 'neutral'
                  }
                };
              });

              // Update price history
              setPriceHistory(prev => ({
                ...prev,
                [symbol]: [...(prev[symbol] || []).slice(-99), newBid]
              }));
            }
          } catch (e) {
            console.warn(`Failed to get price for ${symbol}:`, e.message);
          }
        }

        if (!anySuccess) {
          console.warn('No prices fetched successfully');
        }
      } catch (err) {
        console.warn('Price fetch error:', err.message);
      }
    };

    // Initial fetch when connected
    if (connectionStatus === 'Connected') {
      console.log('Connection established, fetching prices...');
      fetchRealPrices();
    }

    const interval = setInterval(fetchRealPrices, 4000);
    return () => clearInterval(interval);
  }, [connectionStatus]);

  // Update market session
  useEffect(() => {
    setCurrentSession(getMarketSession());
    const interval = setInterval(() => setCurrentSession(getMarketSession()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Set up metaApiService callbacks for real-time position tracking
  useEffect(() => {
    // Set up position update callback
    metaApiService.onPositionUpdate((position) => {
      setOpenPositions(metaApiService.getOpenPositions());
    });
    
    // Set up position close callback
    metaApiService.onPositionClose((position) => {
      setOpenPositions(metaApiService.getOpenPositions());
      setTradeLog(metaApiService.getTradeLog());
    });
    
    // Set up trade error callback
    metaApiService.onTradeError((error) => {
      console.error('Trade error:', error);
      showSnackbar(`Trade error: ${error.error}`);
    });
    
    // Set up reconnect callback
    metaApiService.onReconnect((result) => {
      if (result.success) {
        showSnackbar('Reconnected successfully');
        setConnectionStatus('Connected');
      } else {
        showSnackbar(`Reconnection failed: ${result.error}`);
        setConnectionStatus('Disconnected');
      }
    });
    
    // Initial sync
    if (metaApiService.isReady()) {
      metaApiService.syncPositions().then(() => {
        setOpenPositions(metaApiService.getOpenPositions());
        setTradeLog(metaApiService.getTradeLog());
      });
    }
    
    // Periodic sync for UI updates using RPC
    const syncInterval = setInterval(async () => {
      if (accountRef.current) {
        try {
          const rpc = accountRef.current.getRPCConnection();
          await rpc.connect();
          const positions = await rpc.getPositions();
          setOpenPositions(positions);
        } catch (err) {
          console.warn('Position sync error:', err.message);
        }
      }
    }, 2000);
    
    return () => {
      clearInterval(syncInterval);
      // Clean up callbacks on unmount
      metaApiService.onPositionUpdate(null);
      metaApiService.onPositionClose(null);
      metaApiService.onTradeError(null);
      metaApiService.onReconnect(null);
    };
  }, []);

  // Calculate indicators
  useEffect(() => {
    const newIndicators = {};
    Object.entries(priceHistory).forEach(([symbol, prices]) => {
      if (prices && prices.length >= 20) {
        const sma20 = calculateSMA(prices, 20);
        const sma50 = prices.length >= 50 ? calculateSMA(prices, 50) : null;
        const ema9 = calculateEMA(prices, 9);
        const rsi = calculateRSI(prices, 14);
        const volatility = calculateStdDev(prices.slice(-20));
        
        let trend = 'neutral';
        if (ema9 && sma20) {
          if (ema9 > sma20 * 1.001) trend = 'bullish';
          else if (ema9 < sma20 * 0.999) trend = 'bearish';
        }
        
        newIndicators[symbol] = { sma20, sma50, ema9, rsi, volatility, trend };
      }
    });
    setIndicatorValues(newIndicators);
  }, [priceHistory]);

  // Show snackbar
  const showSnackbar = (message) => {
    setSnackbar({ visible: true, message });
    setTimeout(() => setSnackbar({ visible: false, message: '' }), 4000);
  };

  // Handle start/stop robot with graceful shutdown
  const handleStartStop = async () => {
    if (isRunning) {
      // Graceful shutdown: close all open positions before stopping
      const positions = openPositions.filter((p) => p.magic === MAGIC_NUMBER);
      if (positions.length > 0) {
        showSnackbar(`Stopping robot and closing ${positions.length} positions...`);
        try {
          if (accountRef.current) {
            const rpc = accountRef.current.getRPCConnection();
            await rpc.connect();
            
            for (const pos of positions) {
              try {
                await rpc.closePosition(pos.id);
              } catch (err) {
                console.error(`Error closing position ${pos.id}:`, err);
              }
            }
            
            // Refresh positions
            const updatedPositions = await rpc.getPositions();
            setOpenPositions(updatedPositions);
          }
          showSnackbar(`Robot stopped. All positions closed.`);
        } catch (err) {
          console.error('Error closing positions:', err);
          showSnackbar(`Robot stopped but some positions may not have closed.`);
        }
      } else {
        showSnackbar('Robot stopped');
        // Clear individual trades when robot stops
        setIndividualTrades({});
        setLastTradeTime(0);
      }
      stopRobot();
    } else {
      if (!selectedBot) {
        showSnackbar('Please select a bot first');
        return;
      }
      startRobot();
      showSnackbar(`${selectedBot.name} started!`);
    }
  };

  // Trading logic - runs when robot is running
  useEffect(() => {
    if (!isRunning || !accountRef.current) return;
    
    const checkAndCloseSLTP = async (rpc, positions) => {
      // Filter bot positions - check ALL bot positions
      const botPositions = positions.filter((p) => p.magic === MAGIC_NUMBER);
      
      for (const pos of botPositions) {
        try {
          // Get current price for the position's symbol
          const symbol = pos.symbol;
          const price = await rpc.getSymbolPrice(symbol);
          
          if (!price || price.bid == null || price.ask == null) continue;
          
          // Handle both 'type' (POSITION_TYPE_BUY) and 'side' (BUY) properties
          const isBuy = pos.type === 'POSITION_TYPE_BUY' || pos.side === 'BUY';
          const currentPrice = isBuy ? price.bid : price.ask;
          
          // Get user-defined SL/TP values from trade settings
          const userSLUSD = stopLossUSD[symbol] ? parseFloat(stopLossUSD[symbol]) : null;
          const userTPUSD = profitTargetsUSD[symbol] ? parseFloat(profitTargetsUSD[symbol]) : null;
          
          // Calculate profit/loss in USD
          const entryPrice = pos.price || 0;
          const volume = pos.volume || 0.01;
          const isXAU = symbol.includes('XAU') || symbol.includes('GOLD');
          const isJPY = symbol.includes('JPY');
          
          let profitLossUSD = 0;
          if (entryPrice > 0 && currentPrice > 0) {
            const priceDiff = isBuy ? (currentPrice - entryPrice) : (entryPrice - currentPrice);
            // Calculate dollar value: price diff * volume * contract size
            // XAU: 0.01 price = $10 per 0.01 lot = $1000 per lot
            // Standard: 0.00001 price = $10 per standard lot
            if (isXAU) {
              profitLossUSD = priceDiff * volume * 1000; // $1000 per 1.0 lot per 0.01 move
            } else if (isJPY) {
              profitLossUSD = priceDiff * volume * 100000 * 0.01; // JPY conversion
            } else {
              profitLossUSD = priceDiff * volume * 100000;
            }
          }
          
          let closed = false;
          let reason = '';
          
          // Check user-defined Stop Loss - close if loss equals or exceeds SL value
          if (userSLUSD !== null && userSLUSD > 0) {
            if (profitLossUSD <= -userSLUSD) {
              closed = true;
              reason = `SL HIT (Loss: ${profitLossUSD.toFixed(2)} >= -${userSLUSD})`;
              console.log(`Position ${pos.id}: SL triggered - Loss ${profitLossUSD.toFixed(2)} >= -${userSLUSD}`);
            }
          }
          
          // Check user-defined Take Profit - close if profit equals or exceeds TP value
          if (!closed && userTPUSD !== null && userTPUSD > 0) {
            if (profitLossUSD >= userTPUSD) {
              closed = true;
              reason = `TP HIT (Profit: ${profitLossUSD.toFixed(2)} >= ${userTPUSD})`;
              console.log(`Position ${pos.id}: TP triggered - Profit ${profitLossUSD.toFixed(2)} >= ${userTPUSD}`);
            }
          }
          
          // Check broker SL/TP levels as fallback (only if user SL/TP not set)
          if (!closed && !userSLUSD && pos.stopLoss) {
            if (isBuy && currentPrice <= pos.stopLoss) {
              closed = true;
              reason = 'BROKER_SL';
            } else if (!isBuy && currentPrice >= pos.stopLoss) {
              closed = true;
              reason = 'BROKER_SL';
            }
          }
          
          if (!closed && !userTPUSD && pos.takeProfit) {
            if (isBuy && currentPrice >= pos.takeProfit) {
              closed = true;
              reason = 'BROKER_TP';
            } else if (!isBuy && currentPrice <= pos.takeProfit) {
              closed = true;
              reason = 'BROKER_TP';
            }
          }
          
          if (closed) {
            try {
              console.log(`Closing position ${pos.id} - ${reason} at ${currentPrice}`);
              await rpc.closePosition(pos.id);
              showSnackbar(`${reason}: Closed ${symbol} at ${currentPrice.toFixed(2)} (P/L: ${profitLossUSD.toFixed(2)})`);
              
              // Remove from individual trades tracking
              setIndividualTrades(prev => {
                const updated = { ...prev };
                delete updated[pos.id];
                return updated;
              });
            } catch (closeError) {
              console.error(`Failed to close position ${pos.id}:`, closeError.message);
              showSnackbar(`Error closing trade: ${closeError.message}`);
            }
          } else {
            // Update individual trade with current price and profit
            setIndividualTrades(prev => {
              if (prev[pos.id]) {
                return {
                  ...prev,
                  [pos.id]: {
                    ...prev[pos.id],
                    currentPrice: currentPrice,
                    profit: profitLossUSD,
                    updateTime: new Date().toISOString()
                  }
                };
              }
              return prev;
            });
          }
        } catch (err) {
          console.error(`Error checking SL/TP for position ${pos.id}:`, err.message);
        }
      }
    };
    
    const executeTrade = async () => {
      // GUARD: Only execute trades when robot is actually running
      if (!isRunning) {
        console.log('[Trade] Robot not running - skipping trade execution');
        return;
      }
      
      // Wait for account to be ready
      let retries = 0;
      const maxRetries = 30;
      
      while (!accountRef.current && retries < maxRetries) {
        await new Promise(r => setTimeout(r, 1000));
        retries++;
      }
      
      if (!accountRef.current) {
        console.error('Account not ready');
        return;
      }
      
      try {
        console.log('Executing trade batch...');
        const rpc = accountRef.current.getRPCConnection();
        await rpc.connect();
        
        // Get positions using RPC for accurate tracking
        let positions = await rpc.getPositions();
        
        // Check and close positions with SL/TP hit
        if (localSettings.useSLTP !== false) {
          await checkAndCloseSLTP(rpc, positions);
        }
        
        // Refresh positions after checking SL/TP
        positions = await rpc.getPositions();
        
        // Filter to only count positions opened by this bot
        let botPositions = positions.filter((p) => p.magic === MAGIC_NUMBER);
        
        // Check max trades
        const maxTrades = parseInt(localSettings.maxOpenTrades) || 3;
        const tradesToOpen = maxTrades - 1 - botPositions.length;
        
        if (tradesToOpen <= 0) {
          console.log('Max trades reached (need 1 slot)');
          return;
        }
        
        // Open multiple trades with 3-second delay between each
        for (let i = 0; i < tradesToOpen; i++) {
          // Refresh positions before each trade
          positions = await rpc.getPositions();
          botPositions = positions.filter((p) => p.magic === MAGIC_NUMBER);
          
          if (botPositions.length >= maxTrades) {
            console.log('Max trades reached during batch');
            break;
          }
          
          // Get current price for the symbol
          const symbol = selectedBot?.symbol || 'XAUUSD';
          
          // Check for existing positions on this symbol to prevent opposite direction
          const symbolPositions = getPositionsOnSymbol(positions, symbol, MAGIC_NUMBER);
          const hasBuy = symbolPositions.some(p => (p.type === 'POSITION_TYPE_BUY' || p.side === 'BUY') && !p._closing);
          const hasSell = symbolPositions.some(p => (p.type === 'POSITION_TYPE_SELL' || p.side === 'SELL') && !p._closing);
          
          console.log(`[Trade Check] ${symbol} - Existing Buys: ${hasBuy ? 1 : 0}, Sells: ${hasSell ? 1 : 0}`);
          
          const price = await rpc.getSymbolPrice(symbol);
          
          if (!price || price.bid == null) {
            console.log('No price available');
            continue;
          }
          
          // Determine direction based on settings
          let direction = localSettings.action; // 'BUY', 'SELL', or 'BOTH'
          
          if (direction === 'BOTH') {
            // Prevent opening opposite direction if one already exists
            if (hasBuy && !hasSell) {
              direction = 'SELL';
            } else if (hasSell && !hasBuy) {
              direction = 'BUY';
            } else if (hasBuy && hasSell) {
              console.log(`Both BUY and SELL already open on ${symbol}, skipping`);
              continue;
            } else {
              direction = Math.random() > 0.5 ? 'BUY' : 'SELL';
            }
          }
          
          // Prevent duplicate direction
          if (direction === 'BUY' && hasBuy) {
            console.log(`BUY already exists on ${symbol}, skipping`);
            continue;
          }
          if (direction === 'SELL' && hasSell) {
            console.log(`SELL already exists on ${symbol}, skipping`);
            continue;
          }
          
          const volume = parseFloat(localSettings.lotSize) || 0.01;
          
          // Get SL/TP values from settings
          const useSLTP = localSettings.useSLTP !== false;
          const slUSD = useSLTP ? stopLossUSD[symbol] : null;
          const tpUSD = useSLTP ? profitTargetsUSD[symbol] : null;
          
          // Calculate SL/TP in price points (not USD)
          // For XAUUSD: 1 point = 0.01, 1 lot = 100 oz, so $10 SL = 10/100 = 0.1 points = 0.001 price
          const entryPrice = direction === 'BUY' ? price.ask : price.bid;
          const isXAU = symbol.includes('XAU') || symbol.includes('GOLD');
          const isJPY = symbol.includes('JPY');
          
          // Calculate SL/TP price levels
          let stopLossPrice = null;
          let takeProfitPrice = null;
          
          // Validate SL/TP before opening trade
          let validationErrors = [];
          
          if (useSLTP && slUSD && tpUSD) {
            const slValue = parseFloat(slUSD);
            const tpValue = parseFloat(tpUSD);
            const slPriceMovement = isXAU ? slValue * 0.01 : (isJPY ? slValue * 0.001 : slValue * 0.00001);
            const tpPriceMovement = isXAU ? tpValue * 0.01 : (isJPY ? tpValue * 0.001 : tpValue * 0.00001);
            
            const tempSL = direction === 'BUY' ? entryPrice - slPriceMovement : entryPrice + slPriceMovement;
            const tempTP = direction === 'BUY' ? entryPrice + tpPriceMovement : entryPrice - tpPriceMovement;
            
            // Validate SL/TP distance
            const distanceResult = validateSLTPDistance(symbol, entryPrice, tempSL, tempTP, direction);
            if (!distanceResult.valid) {
              validationErrors.push(...distanceResult.errors);
            }
            
            // Validate max risk using actual account balance
            const riskResult = validateMaxRisk(symbol, volume, entryPrice, tempSL, accountBalance || 10000);
            if (!riskResult.valid) {
              validationErrors.push(...riskResult.errors);
            } else {
              console.log(`Risk validation: ${riskResult.riskPercent}% (Max: 2%)`);
            }
          }
          
          // Show validation errors if any
          if (validationErrors.length > 0) {
            console.warn(`SL/TP validation failed for ${symbol}:`, validationErrors);
            showSnackbar(`Warning: ${validationErrors[0]}`);
            // Continue with trade but log warning - broker will reject if too close
          }
          
          if (useSLTP && slUSD) {
            const slValue = parseFloat(slUSD);
            // Calculate price movement for the SL amount
            // For XAU: $10 per 0.01 lot = $1 per 0.01 price move = $1000 per 1.0 lot
            // $1 move = 100 pips (1 pip = 0.01)
            // So $10 SL = 10 * 0.01 = 0.1 price movement for 0.01 lot
            // Formula: slValue * 0.01 (since $1 per 0.01 = 0.01 * slValue/1)
            const slPriceMovement = isXAU ? slValue * 0.01 : (isJPY ? slValue * 0.001 : slValue * 0.00001);
            stopLossPrice = direction === 'BUY' 
              ? entryPrice - slPriceMovement 
              : entryPrice + slPriceMovement;
          }
          if (useSLTP && tpUSD) {
            const tpValue = parseFloat(tpUSD);
            const tpPriceMovement = isXAU ? tpValue * 0.01 : (isJPY ? tpValue * 0.001 : tpValue * 0.00001);
            takeProfitPrice = direction === 'BUY' 
              ? entryPrice + tpPriceMovement 
              : entryPrice - tpPriceMovement;
          }
          
          // Open trade using RPC connection
          const orderOptions = {
            magic: MAGIC_NUMBER,
            comment: 'CapitalWave Bot',
          };
          
          // Add SL/TP price levels if enabled
          if (useSLTP && stopLossPrice) {
            orderOptions.stopLoss = parseFloat(stopLossPrice.toFixed(isXAU ? 2 : isJPY ? 3 : 5));
          }
          if (useSLTP && takeProfitPrice) {
            orderOptions.takeProfit = parseFloat(takeProfitPrice.toFixed(isXAU ? 2 : isJPY ? 3 : 5));
          }
          
          if (direction === 'BUY') {
            await rpc.createMarketBuyOrder(symbol, volume, orderOptions);
            showSnackbar(`Opened BUY ${symbol} with SL: ${slUSD} TP: ${tpUSD}`);
          } else {
            await rpc.createMarketSellOrder(symbol, volume, orderOptions);
            showSnackbar(`Opened SELL ${symbol} with SL: ${slUSD} TP: ${tpUSD}`);
          }
          
          console.log(`Trade opened: ${direction} ${symbol} ${volume} lots at ${entryPrice} with SL: ${orderOptions.stopLoss} TP: ${orderOptions.takeProfit}`);
          
          // Update last trade time for 3-second delay
          setLastTradeTime(Date.now());
          
          // Refresh positions after trade
          const updatedPositions = await rpc.getPositions();
          setOpenPositions(updatedPositions);
          
          // Track individual trade details
          const newBotPositions = updatedPositions.filter((p) => p.magic === MAGIC_NUMBER);
          const latestPosition = newBotPositions.find(p => 
            p.symbol === symbol && 
            Math.abs(p.volume - volume) < 0.01 &&
            !individualTrades[p.id]
          );
          
          if (latestPosition) {
            setIndividualTrades(prev => ({
              ...prev,
              [latestPosition.id]: {
                id: latestPosition.id,
                symbol: latestPosition.symbol,
                side: latestPosition.side || latestPosition.type,
                entryPrice: entryPrice,
                currentPrice: entryPrice,
                stopLoss: orderOptions.stopLoss,
                takeProfit: orderOptions.takeProfit,
                positionSize: volume,
                profit: 0,
                openTime: new Date().toISOString(),
                updateTime: new Date().toISOString()
              }
            }));
          }
          
          // Wait 3 seconds before opening next trade in batch
          if (i < tradesToOpen - 1) {
            await new Promise(r => setTimeout(r, 3000));
          }
        }
        
      } catch (err) {
        console.error('Trade error:', err.message);
      }
    };
    
    // Run immediately then on interval
    executeTrade();
    
    const intervalMs = (parseInt(localSettings.tradeEveryMinutes) || 5) * 60 * 1000;
    const interval = setInterval(executeTrade, intervalMs);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, selectedBot, localSettings, stopLossUSD, profitTargetsUSD, lastTradeTime]);
  
  // Separate effect for real-time price updates and SL/TP monitoring on individual trades
  useEffect(() => {
    if (!accountRef.current) return;
    
    const updateTradePricesAndCheckSLTP = async () => {
      try {
        const rpc = accountRef.current.getRPCConnection();
        await rpc.connect();
        const positions = await rpc.getPositions();
        const botPositions = positions.filter((p) => p.magic === MAGIC_NUMBER);
        
        // Check and close trades with SL/TP hit - using user-defined USD values
        // Debug: Log positions found
        if (botPositions.length > 0) {
          console.log(`[SLTP] Found ${botPositions.length} bot positions to check`);
        }
        
        for (const pos of botPositions) {
          try {
            const symbol = pos.symbol;
            const price = await rpc.getSymbolPrice(symbol);
            
            if (!price || price.bid == null || price.ask == null) {
              console.warn(`[SLTP] No price for ${symbol}, skipping`);
              continue;
            }
            
            const isBuy = pos.type === 'POSITION_TYPE_BUY' || pos.side === 'BUY';
            const currentPrice = isBuy ? price.bid : price.ask;
            
            // Calculate profit/loss in USD
            const entryPrice = pos.price || 0;
            const volume = pos.volume || 0.01;
            const isXAU = symbol.includes('XAU') || symbol.includes('GOLD');
            const isJPY = symbol.includes('JPY');
            
            let profitLossUSD = 0;
            if (entryPrice > 0 && currentPrice > 0) {
              const priceDiff = isBuy ? (currentPrice - entryPrice) : (entryPrice - currentPrice);
              if (isXAU) {
                profitLossUSD = priceDiff * volume * 1000;
              } else if (isJPY) {
                profitLossUSD = priceDiff * volume * 100000 * 0.01;
              } else {
                profitLossUSD = priceDiff * volume * 100000;
              }
            }
            
            // Debug: Log SL/TP settings being used
            const userSLUSD = stopLossUSD[symbol] ? parseFloat(stopLossUSD[symbol]) : null;
            const userTPUSD = profitTargetsUSD[symbol] ? parseFloat(profitTargetsUSD[symbol]) : null;
            console.log(`[SLTP] ${symbol} P/L: ${profitLossUSD.toFixed(2)} | SL: ${userSLUSD} | TP: ${userTPUSD} | Entry: ${entryPrice} | Current: ${currentPrice}`);
            
            // Use broker-set SL/TP levels from the position
            const stopLossLevel = pos.stopLoss;
            const takeProfitLevel = pos.takeProfit;
            
            let closed = false;
            let reason = '';
            
            // Check user-defined Stop Loss - close if loss equals or exceeds SL value
            if (userSLUSD !== null && userSLUSD > 0 && profitLossUSD <= -userSLUSD) {
              closed = true;
              reason = `SL HIT (Loss: ${profitLossUSD.toFixed(2)} >= -${userSLUSD})`;
              console.log(`[SLTP] Position ${pos.id}: SL triggered - Loss ${profitLossUSD.toFixed(2)} >= -${userSLUSD}`);
            }
            // Check user-defined Take Profit - close if profit equals or exceeds TP value
            if (userTPUSD !== null && userTPUSD > 0 && profitLossUSD >= userTPUSD) {
              closed = true;
              reason = `TP HIT (Profit: ${profitLossUSD.toFixed(2)} >= ${userTPUSD})`;
              console.log(`[SLTP] Position ${pos.id}: USER_TP triggered - Profit ${profitLossUSD.toFixed(2)} >= ${userTPUSD}`);
            }
            // Check broker TP
            else if (!closed && takeProfitLevel) {
              if (isBuy && currentPrice >= takeProfitLevel) {
                closed = true;
                reason = 'BROKER_TP';
                console.log(`[SLTP] Position ${pos.id}: BROKER_TP hit - ${currentPrice} >= ${takeProfitLevel}`);
              } else if (!isBuy && currentPrice <= takeProfitLevel) {
                closed = true;
                reason = 'BROKER_TP';
                console.log(`[SLTP] Position ${pos.id}: BROKER_TP hit - ${currentPrice} <= ${takeProfitLevel}`);
              }
            }
            
            // Check broker SL levels (only if user SL not set)
            if (!closed && !userSLUSD && stopLossLevel) {
              if (isBuy && currentPrice <= stopLossLevel) {
                closed = true;
                reason = 'BROKER_SL';
                console.log(`[SLTP] Position ${pos.id}: BROKER_SL hit - ${currentPrice} <= ${stopLossLevel}`);
              } else if (!isBuy && currentPrice >= stopLossLevel) {
                closed = true;
                reason = 'BROKER_SL';
                console.log(`[SLTP] Position ${pos.id}: BROKER_SL hit - ${currentPrice} >= ${stopLossLevel}`);
              }
            }
            
            if (closed) {
              console.log(`[SLTP] Auto-closing position ${pos.id} - ${reason} at ${currentPrice}`);
              await rpc.closePosition(pos.id);
              showSnackbar(`${reason}: Closed ${symbol} at ${currentPrice.toFixed(2)} (P/L: ${profitLossUSD.toFixed(2)})`);
              
              // Remove from individual trades tracking
              setIndividualTrades(prev => {
                const updated = { ...prev };
                delete updated[pos.id];
                return updated;
              });
              
              // Refresh positions after closing
              const updatedPositions = await rpc.getPositions();
              setOpenPositions(updatedPositions);
              continue;
            }
            
            // Update individual trade with current price and profit
            setIndividualTrades(prev => {
              if (prev[pos.id]) {
                return {
                  ...prev,
                  [pos.id]: {
                    ...prev[pos.id],
                    currentPrice: currentPrice,
                    profit: profitLossUSD,
                    stopLoss: stopLossLevel,
                    takeProfit: takeProfitLevel,
                    updateTime: new Date().toISOString()
                  }
                };
              } else {
                // Create new trade entry if not exists
                return {
                  ...prev,
                  [pos.id]: {
                    id: pos.id,
                    symbol: pos.symbol,
                    side: pos.side || pos.type,
                    entryPrice: pos.price || currentPrice,
                    currentPrice: currentPrice,
                    stopLoss: pos.stopLoss,
                    takeProfit: pos.takeProfit,
                    positionSize: pos.volume,
                    profit: profitLossUSD,
                    openTime: pos.time || new Date().toISOString(),
                    updateTime: new Date().toISOString()
                  }
                };
              }
            });
          } catch (err) {
            console.warn(`Error checking SL/TP for trade ${pos.id}:`, err.message);
          }
        }
        
        // Clean up trades that no longer exist
        setIndividualTrades(prev => {
          const activePositionIds = new Set(botPositions.map(p => p.id));
          const cleaned = { ...prev };
          let changed = false;
          
          Object.keys(cleaned).forEach(tradeId => {
            if (!activePositionIds.has(tradeId)) {
              delete cleaned[tradeId];
              changed = true;
            }
          });
          
          return changed ? cleaned : prev;
        });
        
      } catch (err) {
        console.warn('Error in SL/TP monitoring:', err.message);
      }
    };
    
    // Update prices and check SL/TP every 1 second
    const priceInterval = setInterval(updateTradePricesAndCheckSLTP, 1000);
    
    return () => clearInterval(priceInterval);
  }, [isRunning, connectionStatus, stopLossUSD, profitTargetsUSD]);

  // Save settings
  const saveSettings = () => {
    const newSettings = {
      lotSize: parseFloat(localSettings.lotSize) || 0.01,
      action: localSettings.action,
      tradeEveryMinutes: parseInt(localSettings.tradeEveryMinutes) || 5,
      triggerPoints: parseInt(localSettings.triggerPoints) || 10,
      riskPercent: parseFloat(localSettings.riskPercent) || 1,
      maxOpenTrades: parseInt(localSettings.maxOpenTrades) || 3,
      useSLTP: localSettings.useSLTP,
      profitTargetsUSD,
      stopLossUSD,
    };
    setTradeSettings(newSettings);
    showSnackbar('Settings saved successfully');
  };

  // Handle bot selection
  const handleSelectBot = (bot) => {
    setSelectedBot(bot);
    showSnackbar(`${bot.name} selected`);
  };

  // Force close a single position
  const handleForceClose = async (positionId) => {
    try {
      if (!accountRef.current) {
        throw new Error('Not connected to MetaApi');
      }
      
      showSnackbar(`Force closing position ${positionId}...`);
      
      const rpc = accountRef.current.getRPCConnection();
      await rpc.connect();
      await rpc.closePosition(positionId);
      
      // Refresh positions
      const positions = await rpc.getPositions();
      setOpenPositions(positions);
      
      showSnackbar(`Position ${positionId} closed successfully`);
    } catch (err) {
      console.error('Force close failed:', err);
      showSnackbar(`Force close failed: ${err.message}`);
    }
  };

  // Close all positions (manual) - eslint-disable-next-line no-unused-vars
  // const handleCloseAllPositions = async () => {
  //   try {
  //     if (!accountRef.current) {
  //       throw new Error('Not connected to MetaApi');
  //     }
      
  //     showSnackbar('Closing all positions...');
      
  //     const rpc = accountRef.current.getRPCConnection();
  //     await rpc.connect();
      
  //     // Get all positions
  //     const positions = await rpc.getPositions();
      
  //     // Filter to bot positions only
  //     const botPositions = positions.filter((p) => p.magic === MAGIC_NUMBER);
      
  //     let closed = 0;
  //     let failed = 0;
      
  //     for (const pos of botPositions) {
  //       try {
  //         await rpc.closePosition(pos.id);
  //         closed++;
  //       } catch (err) {
  //         console.error(`Failed to close position ${pos.id}:`, err);
  //         failed++;
  //       }
  //     }
      
  //     // Refresh positions
  //     const updatedPositions = await rpc.getPositions();
  //     setOpenPositions(updatedPositions);
      
  //     showSnackbar(`Closed ${closed} positions. Failed: ${failed}`);
  //   } catch (err) {
  //     console.error('Close all failed:', err);
  //     showSnackbar(`Close all failed: ${err.message}`);
  //   }
  // };

  // Render connection status banner
  const renderConnectionStatus = () => {
    if (!mtAccount) {
      return (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-amber-500" />
          <div>
            <p className="font-semibold text-theme">No Account Connected</p>
            <p className="text-sm text-theme-secondary">Connect your MT5 account to start trading</p>
          </div>
        </div>
      );
    }
    if (!isLicenseValid) {
      return (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-500" />
          <div>
            <p className="font-semibold text-theme">License Required</p>
            <p className="text-sm text-theme-secondary">Activate your license to trade</p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Render tab button
  const TabButton = ({ tab, label, icon: Icon }) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`tab-button flex items-center gap-2 ${activeTab === tab ? 'tab-active' : 'tab-inactive'}`}
    >
      <Icon className="w-5 h-5" />
      <span>{label}</span>
    </button>
  );

  // Render bot selector
  const renderBotSelector = () => (
    <div className="card mb-6">
      <h3 className="text-lg font-bold text-theme mb-4 flex items-center gap-2">
        <Bot className="w-5 h-5 text-ocean-light" />
        Trading Robot
      </h3>
      
      <select 
        className="input-field mb-4"
        value={selectedBot?.id || ''}
        onChange={(e) => {
          const bot = AVAILABLE_BOTS.find(b => b.id === e.target.value);
          handleSelectBot(bot);
        }}
      >
        <option value="">Select a trading strategy</option>
        {AVAILABLE_BOTS.map(bot => (
          <option key={bot.id} value={bot.id}>
            {bot.name} - {bot.description}
          </option>
        ))}
      </select>

      {/* Status indicator */}
      <div className="flex items-center justify-center gap-2 bg-theme-muted rounded-full py-2 px-4">
        <div className={`status-dot ${isRunning ? 'status-active' : 'status-inactive'}`} />
        <span className={`text-sm font-semibold ${isRunning ? 'risk-low' : 'text-red-500'}`}>
          {isRunning ? 'RUNNING' : 'STOPPED'}
        </span>
      </div>
      
      <p className="text-xs text-theme-muted text-center mt-3">
        Go to Status tab to start/stop the robot
      </p>
    </div>
  );

  // Render settings section
  const renderSettingsSection = () => (
    <div className="card">
      {/* Settings Header - Clickable */}
      <button
        onClick={() => setSettingsExpanded(!settingsExpanded)}
        className="w-full flex items-center justify-between p-2"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-ocean-light/15 flex items-center justify-center">
            <Settings className="w-5 h-5 text-ocean-light" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-theme">Trading Settings</h3>
            <p className="text-sm text-theme-secondary">Configure risk, timing, SL & TP</p>
          </div>
        </div>
        {settingsExpanded ? <ChevronUp className="w-5 h-5 text-theme-muted" /> : <ChevronDown className="w-5 h-5 text-theme-muted" />}
      </button>

      {/* Expanded Settings Content */}
      {settingsExpanded && (
        <div className="mt-6 pt-6 border-t border-theme space-y-6">
          {/* Trade Direction */}
          <div>
            <label className="block text-sm font-semibold text-theme mb-2">Trade Direction</label>
            <select 
              className="input-field"
              value={localSettings.action}
              onChange={(e) => setLocalSettings(prev => ({ ...prev, action: e.target.value }))}
            >
              <option value="BUY">Buy Only</option>
              <option value="SELL">Sell Only</option>
              <option value="BOTH">Both</option>
            </select>
          </div>

          {/* Lot Size */}
          <div>
            <label className="block text-sm font-semibold text-theme mb-2">Lot Size</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.01"
                min="0.01"
                className="input-field flex-1"
                value={localSettings.lotSize}
                onChange={(e) => setLocalSettings(prev => ({ ...prev, lotSize: e.target.value }))}
              />
              <span className="text-theme-secondary font-medium">lots</span>
            </div>
          </div>

          {/* Max Trades */}
          <div>
            <label className="block text-sm font-semibold text-theme mb-2">Max Concurrent Trades</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="10"
                className="input-field flex-1"
                value={localSettings.maxOpenTrades}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  if (val >= 1 && val <= 10) {
                    setLocalSettings(prev => ({ ...prev, maxOpenTrades: e.target.value }));
                  }
                }}
              />
              <span className="text-theme-secondary font-medium">trades</span>
            </div>
            <p className="text-xs text-theme-muted mt-1">Valid range: 1-10 trades</p>
          </div>

          {/* SL & TP per symbol */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-ocean-light" />
              <h4 className="font-semibold text-theme">Symbol SL & TP (USD)</h4>
            </div>
            
            {DESIRED_SYMBOLS.map(symbol => (
              <div key={symbol} className="mb-4">
                <button
                  onClick={() => setExpandedSymbol(expandedSymbol === symbol ? null : symbol)}
                  className="w-full flex items-center justify-between p-3 bg-theme-muted rounded-xl hover:bg-theme-surfaceHover transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-ocean-light/10 flex items-center justify-center">
                      {symbol === 'XAUUSD' ? <DollarSign className="w-4 h-4 text-ocean-light" /> : 
                       symbol === 'BTCUSD' ? <LineChart className="w-4 h-4 text-ocean-light" /> :
                       <BarChart3 className="w-4 h-4 text-ocean-light" />}
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-theme">{symbol}</p>
                      <p className="text-xs text-theme-secondary">TP: ${profitTargetsUSD[symbol]} • SL: ${stopLossUSD[symbol]}</p>
                    </div>
                  </div>
                  {expandedSymbol === symbol ? <ChevronUp className="w-5 h-5 text-theme-muted" /> : <ChevronDown className="w-5 h-5 text-theme-muted" />}
                </button>
                
                {expandedSymbol === symbol && (
                  <div className="mt-3 p-4 bg-theme-muted rounded-xl space-y-4">
                    {/* Take Profit */}
                    <div>
                      <p className="text-sm font-semibold text-theme mb-2">Take Profit (USD)</p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setProfitTargetsUSD(prev => ({
                            ...prev,
                            [symbol]: Math.max(1, parseInt(prev[symbol]) - 1).toString()
                          }))}
                          className="w-10 h-10 rounded-lg bg-ocean-light text-white flex items-center justify-center"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <input
                          type="number"
                          min="1"
                          className="input-field flex-1 text-center font-bold text-green-600"
                          value={profitTargetsUSD[symbol]}
                          onChange={(e) => setProfitTargetsUSD(prev => ({
                            ...prev,
                            [symbol]: e.target.value
                          }))}
                        />
                        <button
                          onClick={() => setProfitTargetsUSD(prev => ({
                            ...prev,
                            [symbol]: (parseInt(prev[symbol]) + 1).toString()
                          }))}
                          className="w-10 h-10 rounded-lg bg-ocean-light text-white flex items-center justify-center"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Stop Loss */}
                    <div>
                      <p className="text-sm font-semibold text-theme mb-2">Stop Loss (USD)</p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setStopLossUSD(prev => ({
                            ...prev,
                            [symbol]: Math.max(1, parseInt(prev[symbol]) - 1).toString()
                          }))}
                          className="w-10 h-10 rounded-lg bg-red-500 text-white flex items-center justify-center"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <input
                          type="number"
                          min="1"
                          className="input-field flex-1 text-center font-bold text-red-500"
                          value={stopLossUSD[symbol]}
                          onChange={(e) => setStopLossUSD(prev => ({
                            ...prev,
                            [symbol]: e.target.value
                          }))}
                        />
                        <button
                          onClick={() => setStopLossUSD(prev => ({
                            ...prev,
                            [symbol]: (parseInt(prev[symbol]) + 1).toString()
                          }))}
                          className="w-10 h-10 rounded-lg bg-red-500 text-white flex items-center justify-center"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Use SL/TP Toggle */}
          <div className="flex items-center justify-between py-3 border-t border-theme">
            <div>
              <p className="font-semibold text-theme">Use SL & TP</p>
              <p className="text-xs text-theme-secondary">Enable take profit and stop loss</p>
            </div>
            <button
              onClick={() => setLocalSettings(prev => ({ ...prev, useSLTP: !prev.useSLTP }))}
              className={`w-12 h-6 rounded-full transition-colors ${localSettings.useSLTP ? 'bg-ocean-light' : 'bg-dark-border'}`}
            >
              <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${localSettings.useSLTP ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* SL/TP Distance Requirements Info */}
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <p className="text-xs font-semibold text-amber-500 mb-2">Minimum Distance Requirements</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {Object.entries(MIN_SL_TP_DISTANCE).map(([symbol, minPips]) => (
                <div key={symbol} className="flex justify-between">
                  <span className="text-theme-secondary">{symbol}:</span>
                  <span className="text-theme font-medium">{minPips} pips</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-theme-secondary mt-2">Max risk per trade: 2% of account balance</p>
          </div>

          {/* Save Button */}
          <button onClick={saveSettings} className="btn-primary w-full flex items-center justify-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Save Settings & SL/TP
          </button>
        </div>
      )}
    </div>
  );

  // Render status tab
  const renderStatusTab = () => {
    // Use state-based openPositions for accurate count that triggers re-renders
    // Filter to only count positions opened by this bot (magic number)
    const botPositions = openPositions.filter((p) => p.magic === MAGIC_NUMBER);
    const openTradesCount = botPositions.length;
    
    // Calculate total open P/L
    const totalOpenPL = botPositions.reduce((sum, p) => sum + (p.profit ?? 0), 0);

    return (
      <>
        {/* Robot Control Card */}
        <div className="card mb-6">
          {/* Robot Status Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className={`status-dot w-2.5 h-2.5 ${isRunning ? 'status-active' : 'status-inactive'}`} />
              <span className={`text-xs font-bold tracking-wider ${isRunning ? 'risk-low' : 'text-theme-muted'}`}>
                {isRunning ? 'ACTIVE' : 'INACTIVE'}
              </span>
            </div>
            {selectedBot && (
              <div className="flex items-center gap-2 bg-ocean-light/10 px-3 py-1.5 rounded-full">
                <Bot className="w-4 h-4 text-ocean-light" />
                <span className="text-sm font-medium text-theme">{selectedBot.name}</span>
              </div>
            )}
          </div>

          {/* SL/TP Display */}
          {selectedBot && (
            <div className="mb-4 p-2 bg-theme-muted rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-xs text-theme-secondary">Stop Loss</p>
                  <p className="font-bold text-red-400">${stopLossUSD[selectedBot?.symbol] || 10}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-theme-secondary">Take Profit</p>
                  <p className="font-bold text-green-400">${profitTargetsUSD[selectedBot?.symbol] || 7}</p>
                </div>
              </div>
             
            </div>
          )}

          {/* Main Control */}
          <div className="mb-5">
            <h2 className="text-2xl font-bold text-theme mb-1">
              {isRunning ? 'Robot is Trading' : 'Start Trading Robot'}
            </h2>
            <p className="text-theme-secondary">
              {isRunning 
                ? `${openTradesCount} of ${localSettings.maxOpenTrades || 3} trades open`
                : selectedBot ? 'Ready to begin automated trading' : 'Select a bot to get started'
              }
            </p>
          </div>

          <button
            onClick={handleStartStop}
            disabled={!selectedBot && !isRunning}
            className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all
              ${isRunning 
                ? 'bg-red-500 hover:bg-red-600 text-white' 
                : 'bg-gradient-to-r from-ocean-light to-teal-500 hover:shadow-ocean-lg text-white'
              }
              ${!selectedBot && !isRunning ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {isRunning ? (
              <>
                <Square className="w-5 h-5" />
                STOP ROBOT
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                START ROBOT
              </>
            )}
          </button>
        </div>

        {/* Trades Count Card */}
        <div className="card mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-ocean-light/10 flex items-center justify-center">
              <Activity className="w-7 h-7 text-ocean-light" />
            </div>
            <div>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-ocean-light">{openTradesCount}</span>
                <span className="text-lg text-theme-secondary">/ {localSettings.maxOpenTrades || 3}</span>
              </div>
              <p className="text-sm text-theme-secondary">Open Trades (Max: {localSettings.maxOpenTrades || 3})</p>
            </div>
            {totalOpenPL !== 0 && (
              <div className="ml-auto text-right">
                <p className="text-sm text-theme-secondary">Total P/L</p>
                <p className={`text-2xl font-bold ${(totalOpenPL ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {(totalOpenPL ?? 0) >= 0 ? '+' : ''}{(totalOpenPL ?? 0).toFixed(2)}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Active Positions with Individual Trade Details - Bot Positions Only */}
        {botPositions.length > 0 && (
          <div className="card mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-theme flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-ocean-light" />
                Active Positions ({botPositions.length})
              </h3>
            
            </div>
            <div className="space-y-3">
              {botPositions.map((position) => {
                // Get individual trade details (supplementary - for planned SL/TP not yet set by broker)
                const tradeDetails = individualTrades[position.id] || {};
                
                // Use broker position data as PRIMARY source (always accurate/real-time)
                // Individual trades is only supplementary for planned values
                const entryPrice = (position.price ?? tradeDetails.entryPrice) ?? 0;
                const positionSize = (position.volume ?? tradeDetails.positionSize) ?? 0.01;
                const profit = (position.profit ?? tradeDetails.profit) ?? 0;
                const side = position.type || position.side || 'BUY';
                const isBuy = side === 'BUY' || side === 'POSITION_TYPE_BUY';
                
                // Get current price from live quotes or trade details - prefer live quotes for real-time
                const symbol = position.symbol;
                const liveQuote = liveQuotes[symbol];
                const liveBid = liveQuote?.bid && liveQuote.bid !== '—' ? parseFloat(liveQuote.bid) : 0;
                const liveAsk = liveQuote?.ask && liveQuote.ask !== '—' ? parseFloat(liveQuote.ask) : 0;
                const liveCurrentPrice = (liveBid > 0 && liveAsk > 0) ? (isBuy ? liveAsk : liveBid) : 0;
                const currentPrice = (liveCurrentPrice > 0) ? liveCurrentPrice : ((tradeDetails.currentPrice ?? position.price) ?? 0);
                const stopLoss = position.stopLoss ?? tradeDetails.stopLoss;
                const takeProfit = position.takeProfit ?? tradeDetails.takeProfit;
                
                // Use broker profit if available, otherwise calculate
                let calculatedPL = profit;
                if ((profit === 0 || profit === undefined) && entryPrice > 0 && currentPrice > 0) {
                  const priceDiff = isBuy ? (currentPrice - entryPrice) : (entryPrice - currentPrice);
                  const pointValue = positionSize * 100000;
                  calculatedPL = priceDiff * pointValue;
                }
                
                // Get SL/TP from trade settings (fallback when broker SL/TP not set)
                const slUSD = stopLossUSD[symbol] ? parseFloat(stopLossUSD[symbol]) : null;
                const tpUSD = profitTargetsUSD[symbol] ? parseFloat(profitTargetsUSD[symbol]) : null;
                
                // Calculate SL/TP price levels from trade settings if available
                let settingsStopLoss = null;
                let settingsTakeProfit = null;
                if (slUSD && entryPrice > 0) {
                  const isXAU = symbol === 'XAUUSD' || symbol === 'XAU';
                  const isJPY = symbol.includes('JPY');
                  const slPriceMovement = isXAU ? slUSD * 0.01 : (isJPY ? slUSD * 0.001 : slUSD * 0.00001);
                  settingsStopLoss = isBuy ? entryPrice - slPriceMovement : entryPrice + slPriceMovement;
                }
                if (tpUSD && entryPrice > 0) {
                  const isXAU = symbol === 'XAUUSD' || symbol === 'XAU';
                  const isJPY = symbol.includes('JPY');
                  const tpPriceMovement = isXAU ? tpUSD * 0.01 : (isJPY ? tpUSD * 0.001 : tpUSD * 0.00001);
                  settingsTakeProfit = isBuy ? entryPrice + tpPriceMovement : entryPrice - tpPriceMovement;
                }
                
                // Use broker SL/TP if set, otherwise fall back to trade settings
                const displayStopLoss = stopLoss || settingsStopLoss;
                const displayTakeProfit = takeProfit || settingsTakeProfit;
                
                return (
                  <div key={position.id} className="p-4 bg-theme-muted rounded-lg border border-theme-border">
                    {/* Header Row */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className={`font-bold text-lg ${isBuy ? 'text-green-500' : 'text-red-500'}`}>
                          {isBuy ? 'BUY' : 'SELL'}
                        </span>
                        <span className="font-semibold text-theme text-lg">{position.symbol}</span>
                        <span className="text-theme-secondary">{positionSize} lots</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-bold text-xl ${calculatedPL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {calculatedPL >= 0 ? '+' : ''}{calculatedPL.toFixed(2)}
                        </span>
                        <button
                          onClick={() => handleForceClose(position.id)}
                          className="p-1.5 text-red-500 hover:bg-red-500/20 rounded"
                          title="Close position"
                        >
                          <Square className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Price Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div className="bg-theme-surface p-2 rounded">
                        <p className="text-theme-secondary text-xs mb-1">Entry Price</p>
                        <p className="font-mono font-semibold text-theme">{entryPrice > 0 ? entryPrice.toFixed(5) : '—'}</p>
                      </div>
                      <div className="bg-theme-surface p-2 rounded">
                        <p className="text-theme-secondary text-xs mb-1">Current Price</p>
                        <p className="font-mono font-semibold text-theme">{currentPrice > 0 ? currentPrice.toFixed(5) : '—'}</p>
                      </div>
                      <div className="bg-theme-surface p-2 rounded">
                        <p className="text-theme-secondary text-xs mb-1">Stop Loss</p>
                        <p className="font-mono font-semibold text-red-400">
                          {displayStopLoss ? displayStopLoss.toFixed(5) : '—'}
                        </p>
                      </div>
                      <div className="bg-theme-surface p-2 rounded">
                        <p className="text-theme-secondary text-xs mb-1">Take Profit</p>
                        <p className="font-mono font-semibold text-green-400">
                          {displayTakeProfit ? displayTakeProfit.toFixed(5) : '—'}
                        </p>
                      </div>
                    </div>
                    
                    {/* P/L Bar - Uses selected bot's SL/TP settings */}
                    <div className="mt-3 flex items-center gap-2">
                      <div className="flex-1 h-2 bg-theme-surface rounded-full overflow-hidden">
                        {(() => {
                          // Get selected bot's SL/TP in USD
                          const botSymbol = selectedBot?.symbol;
                          const botSLUSD = botSymbol && stopLossUSD[botSymbol] ? parseFloat(stopLossUSD[botSymbol]) : null;
                          const botTPUSD = botSymbol && profitTargetsUSD[botSymbol] ? parseFloat(profitTargetsUSD[botSymbol]) : null;
                          
                          // Calculate progress towards TP (profit) or SL (loss)
                          let progressPercent = 0;
                          if (botTPUSD && botSLUSD) {
                            if (calculatedPL >= 0) {
                              // In profit - show progress towards TP
                              progressPercent = Math.min((calculatedPL / botTPUSD) * 100, 100);
                            } else {
                              // In loss - show progress towards SL
                              progressPercent = Math.min((Math.abs(calculatedPL) / botSLUSD) * 100, 100);
                            }
                          } else {
                            // Fallback to original calculation
                            progressPercent = Math.min(Math.abs(calculatedPL) / 10 * 100, 100);
                          }
                          
                          return (
                            <div 
                              className={`h-full transition-all duration-300 ${calculatedPL >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                              style={{ width: `${progressPercent}%` }}
                            />
                          );
                        })()}
                      </div>
                      <span className={`text-xs font-medium ${calculatedPL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {calculatedPL >= 0 ? 'PROFIT' : 'LOSS'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Trade Log Toggle */}
        {/* <div className="card mb-6">
          <button
            onClick={() => setShowTradeLog(!showTradeLog)}
            className="w-full flex items-center justify-between"
          >
            <h3 className="font-bold text-theme flex items-center gap-2">
              <Clock className="w-5 h-5 text-ocean-light" />
              Trade Log ({tradeLog.length} events)
            </h3>
            {showTradeLog ? <ChevronUp className="w-5 h-5 text-theme-muted" /> : <ChevronDown className="w-5 h-5 text-theme-muted" />}
          </button>
          {showTradeLog && (
            <div className="mt-4 max-h-64 overflow-y-auto space-y-1">
              {tradeLog.slice(-20).reverse().map((log, index) => (
                <div key={index} className={`text-xs p-2 rounded font-mono
                  ${log.type === 'TRADE_OPENED' ? 'bg-green-500/10 text-green-400' :
                    log.type === 'POSITION_CLOSED' ? 'bg-blue-500/10 text-blue-400' :
                    log.type === 'SL_HIT' || log.type === 'TP_HIT' ? 'bg-yellow-500/10 text-yellow-400' :
                    log.type === 'FORCE_CLOSE_FAILED' ? 'bg-red-500/10 text-red-400' :
                    'bg-theme-muted text-theme-secondary'}
                `}>
                  <span className="text-theme-muted">[{new Date(log.timestamp).toLocaleTimeString()}]</span>{' '}
                  <span className="font-semibold">{log.type}</span>
                  {log.symbol && <span> {log.symbol}</span>}
                  {log.side && <span> {log.side}</span>}
                  {log.error && <span className="text-red-400"> - {log.error}</span>}
                </div>
              ))}
              {tradeLog.length === 0 && (
                <p className="text-theme-muted text-sm text-center py-4">No trade events yet</p>
              )}
            </div>
          )}
        </div> */}

        {/* Market Session & Risk Status */}
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-theme mb-2">Market Session</h3>
              <div className="flex items-center gap-2">
                {currentSession.isActive ? (
                  <Clock className="w-5 h-5 text-green-500" />
                ) : (
                  <Clock className="w-5 h-5 text-theme-muted" />
                )}
                <span className={`font-semibold ${currentSession.isActive ? 'risk-low' : 'text-theme-muted'}`}>
                  {currentSession.name} {currentSession.isActive ? '(Active)' : '(Closed)'}
                </span>
              </div>
            </div>
            <div className="text-right">
              <h3 className="font-bold text-theme mb-2">Risk Status</h3>
              <span className="text-green-500 font-semibold">DD: 0.0%</span>
            </div>
          </div>

          {/* Technical Indicators */}
          {selectedBot && indicatorValues[selectedBot.symbol] && (
            <div className="pt-4 border-t border-theme">
              <h4 className="font-semibold text-theme mb-3">Indicators ({selectedBot.symbol})</h4>
              <div className="grid grid-cols-2 gap-3">
                {(() => {
                  const ind = indicatorValues[selectedBot.symbol];
                  return (
                    <>
                      <div className="bg-theme-muted p-3 rounded-xl">
                        <p className="text-xs text-theme-secondary">Trend</p>
                        <p className={`font-bold ${ind.trend === 'bullish' ? 'risk-low' : ind.trend === 'bearish' ? 'text-red-500' : 'text-theme'}`}>
                          {ind.trend.toUpperCase()}
                        </p>
                      </div>
                      <div className="bg-theme-muted p-3 rounded-xl">
                        <p className="text-xs text-theme-secondary">RSI</p>
                        <p className="font-bold text-theme">{ind.rsi ? ind.rsi.toFixed(1) : '—'}</p>
                      </div>
                      <div className="bg-theme-muted p-3 rounded-xl">
                        <p className="text-xs text-theme-secondary">EMA9</p>
                        <p className="font-bold text-theme">{ind.ema9 ? ind.ema9.toFixed(5) : '—'}</p>
                      </div>
                      <div className="bg-theme-muted p-3 rounded-xl">
                        <p className="text-xs text-theme-secondary">SMA20</p>
                        <p className="font-bold text-theme">{ind.sma20 ? ind.sma20.toFixed(5) : '—'}</p>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Market Quotes */}
        <div className="card">
          <h3 className="text-lg font-bold text-theme mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-ocean-light" />
            Market Quotes
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            {DESIRED_SYMBOLS.map(symbol => {
              const quote = liveQuotes[symbol] || { bid: '—', ask: '—' };
              return (
                <div 
                  key={symbol} 
                  className={`p-4 rounded-xl border transition-all duration-300
                    ${quote.direction === 'up' ? 'quote-up' : 
                      quote.direction === 'down' ? 'quote-down' : 
                      'bg-theme-muted border-theme'}
                  `}
                >
                  <p className="font-bold text-theme mb-2">{symbol}</p>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-xs text-theme-secondary">Bid</span>
                      <span className="font-semibold text-green-600">{quote.bid}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-theme-secondary">Ask</span>
                      <span className="font-semibold text-red-500">{quote.ask}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={`min-h-screen bg-theme`}>
     
      {/* Header */}
      <div className="bg-gradient-to-r from-ocean-light to-teal-500 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight">TidalWave Pro</h1>
                <p className="text-white/80 text-sm">Trading Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors backdrop-blur"
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              <div className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full">
                <div className={`w-2 h-2 rounded-full ${connectionStatus === 'Connected' ? 'bg-green-400' : 'bg-yellow-400'}`} />
                <span className="text-sm font-medium">{connectionStatus}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex gap-3 mb-6">
          <TabButton tab="trade" label="Trade Settings" icon={Settings} />
          <TabButton tab="status" label="Status" icon={Activity} />
        </div>

        {/* Connection Status Banner */}
        {renderConnectionStatus()}

        {/* Tab Content */}
        {activeTab === 'trade' ? (
          <>
            {renderBotSelector()}
            {renderSettingsSection()}
          </>
        ) : (
          renderStatusTab()
        )}
      </div>

      {/* Snackbar */}
      {snackbar.visible && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span>{snackbar.message}</span>
        </div>
      )}
    </div>
  );
}


